import { useCallback, useEffect, useRef, useState } from 'react';
import { FetchResult } from '@apollo/client';

import { useLatestRef } from '../hooks/use-latest-ref';
import { useIsMounted } from '../hooks/use-is-mounted';
import { getCurrUserSession, removeUserSessionActions, submitUserSession } from '../utils/user-session.service';
import { claimSavingScores, releaseSavingScores, markOfflineSyncPending } from '../../components/_core/OfflineHandler/offline-handler.container';
import { IRecordUserActionsResults } from '../../components/Score/ManageEvaluations/manage-evaluations.interface';
import { actionKey, ackedActionKeys, refusedScoreActionKeys, refusedScoreReasons } from '../helpers/user-action-ack.helpers';
import { useRefusedScoresSnackbar } from '../hooks/use-refused-scores-snackbar';
import { IUserActionAck } from '../types';

export interface IRecordUserSessionData {
  recordUserSession: IUserActionAck[];
}

// The submitted session's actions the server acknowledged, by action key (the acked SET + the subset to
// prune). One owner so the interval submit and leave-flush can't drift.
export const partitionAckedActions = (
  userSession: IUserSession,
  recordUserSession: IUserActionAck[],
) => {
  const ackedKeys = ackedActionKeys(userSession.actions ?? [], recordUserSession);
  const actionsToRemove = (userSession.actions ?? []).filter((a) => ackedKeys.has(actionKey(a) as string));
  return { ackedKeys, actionsToRemove };
};

// The RECORD_USER_SESSION mutate function (useMutation tuple-0) the submit/flush paths accept;
// variables are the dynamic session-actions envelope built by submitUserSession.
type RecordUserSessionMutationFn = (
  options: { variables: Record<string, any> } & Record<string, any>,
) => Promise<FetchResult<IRecordUserSessionData>>;

// One owner of the guarded submit lifecycle (isSavingScores claim/release, submit, error log)
// so the leave-flush and the interactive submit can't drift or race on the shared flag.
const submitScoresExclusively = async <T,>(
  user: IUser | null | undefined,
  recordUserSessionMutation: RecordUserSessionMutationFn,
  logLabel: string,
  onSession: (
    submitter: IUser,
    userSession: IUserSession,
    recordUserSession: IUserActionAck[],
  ) => Promise<T> | T,
): Promise<T | undefined> => {
  if (!user || !claimSavingScores()) return undefined;
  try {
    const { userSession, recordUserSession } = await submitUserSession(user, recordUserSessionMutation);
    if (!userSession) return undefined;
    return await onSession(user, userSession, recordUserSession ?? []);
  } catch (err) {
    console.error(logLabel, err);
    return undefined;
  } finally {
    releaseSavingScores();
  }
};

// Fire-and-forget flush of buffered scores on navigate-away so a quick exit (before the 5s
// interval) still delivers; prunes acked + flags consumers to refetch. Safe post-unmount.
export const flushScoresNow = async (
  user: IUser | null | undefined,
  recordUserSessionMutation: RecordUserSessionMutationFn,
  notifyRefused?: (count: number, reasons: string[]) => void,
): Promise<void> => {
  await submitScoresExclusively(user, recordUserSessionMutation, 'flushScoresNow failed:', async (submitter, userSession, recordUserSession) => {
    const { ackedKeys, actionsToRemove } = partitionAckedActions(userSession, recordUserSession);
    notifyRefused?.(refusedScoreActionKeys(userSession.actions ?? [], recordUserSession).size, refusedScoreReasons(userSession.actions ?? [], recordUserSession));
    // Nothing acked = nothing flushed: skip the prune AND the refetch ping — a no-op
    // navigation away must not trigger network refetches in every registered consumer.
    if (!actionsToRemove.length) return;
    await removeUserSessionActions(submitter._id, userSession.startedAt, ackedKeys);
    markOfflineSyncPending();
  });
};

// HOOK: flushes buffered scores when the consuming screen unmounts, so a quick exit (before
// the 5s interval fires) still delivers. Ref indirection: cleanup fires only on unmount.
export const useFlushScoresOnUnmount = (
  user: IUser | null | undefined,
  recordUserSessionMutation: RecordUserSessionMutationFn,
): void => {
  const notifyRefused = useRefusedScoresSnackbar();
  // The ref dep is identity-stable, so the effect still binds once and cleans up on unmount only.
  const flushRef = useLatestRef(() => flushScoresNow(user, recordUserSessionMutation, notifyRefused));
  useEffect(() => () => { flushRef.current(); }, [flushRef]);
};

// HOOK: guarded interactive submit shared by the scoring surfaces — saving-flag lifecycle,
// submit, refetch-on-success, error log. Returns the results useBufferedScoreSubmit consumes.
export const useSubmitScores = (
  user: IUser | null | undefined,
  recordUserSessionMutation: RecordUserSessionMutationFn,
  refetch: () => void,
  logLabel: string,
) =>
  useCallback(
    (): Promise<IRecordUserActionsResults | undefined> =>
      submitScoresExclusively(user, recordUserSessionMutation, logLabel, (submitter, userSession, recordUserSession) => {
        refetch();
        return { user: submitter, userSession, recordUserSession };
      }),
    [user, recordUserSessionMutation, refetch, logLabel],
  );

// True if the NEWEST session (the scope submitUserSession flushes) still holds an
// unsynced action this screen owns; on a read error assume yes so retries keep running.
export const screenHasUnsyncedActions = async (
  user: IUser | null | undefined,
  isMyAction: (action: IUserAction) => boolean,
): Promise<boolean> => {
  if (!user) return false;
  try {
    const session = await getCurrUserSession(user);
    return !!session?.actions?.some(isMyAction);
  } catch (err) {
    console.error('screenHasUnsyncedActions read failed:', err);
    return true; // read failed — assume unsynced so the 5s retry interval keeps running
  }
};

interface IUseBufferedScoreSubmit {
  user?: IUser | null;
  isSaving: boolean;
  isQueueOpen: boolean;
  hasUnsavedScores: boolean;
  setHasUnsavedScores: (value: boolean) => void;
  submitScores: () => Promise<IRecordUserActionsResults | undefined>;
  isMyAction: (action: IUserAction) => boolean;
  canSubmit?: boolean;
}

// Shared submit/cleanup/retry state machine for the buffered-score screens. Submits
// the whole device buffer but gauges success + the retry flag against THIS screen only.
// A struggling server must not be hit harder. The buffer is retained until acknowledged now, so
// without this a failing submit re-POSTs a growing payload every 5s — the client hitting hardest
// exactly when the server can least answer. Reset by any progress, so a recovery is immediate.
export const SUBMIT_RETRY_DELAYS_MS = [5 * 1000, 10 * 1000, 30 * 1000, 60 * 1000];
export const retryDelayMs = (consecutiveFailures: number): number =>
  SUBMIT_RETRY_DELAYS_MS[Math.min(Math.max(consecutiveFailures, 0), SUBMIT_RETRY_DELAYS_MS.length - 1)];

export const useBufferedScoreSubmit = ({
  user,
  isSaving,
  isQueueOpen,
  hasUnsavedScores,
  setHasUnsavedScores,
  submitScores,
  isMyAction,
  canSubmit = true,
}: IUseBufferedScoreSubmit) => {
  const [hasSaveError, setHasSaveError] = useState(false);
  const notifyRefused = useRefusedScoresSnackbar();
  // The 5s interval can have a submit in flight when the screen unmounts (navigate away
  // mid-save); guard its async continuations so they don't setState after teardown.
  const isMounted = useIsMounted();
  // Consecutive submits that achieved nothing, and the instant the next one may run.
  const failuresRef = useRef(0);
  const nextAttemptAtRef = useRef(0);
  const backOff = useCallback(() => {
    failuresRef.current += 1;
    nextAttemptAtRef.current = Date.now() + retryDelayMs(failuresRef.current);
  }, []);
  const resumeFullRate = useCallback(() => {
    failuresRef.current = 0;
    nextAttemptAtRef.current = 0;
  }, []);

  const handleSubmitScores = useCallback(() => {
    if (isSaving) return;

    // hasUnsavedScores is set only from the outcome: cleared up front, a submit that never settled switched the retry off.
    return submitScores()
      .then(async (results) => {
        if (!results) {
          // Nothing came back; re-read to keep the retry interval honest.
          const unsynced = await screenHasUnsyncedActions(user, isMyAction);
          backOff();
          if (!isMounted()) return;
          setHasSaveError(true);
          setHasUnsavedScores(unsynced);
          return;
        }

        // One acked-key set drives both: an action is removable iff the server acknowledged
        // it; this screen failed iff any of ITS actions wasn't.
        const { ackedKeys } = partitionAckedActions(results.userSession, results.recordUserSession ?? []);
        const saveFailed = results.userSession.actions.filter(isMyAction).some((a) => !ackedKeys.has(actionKey(a) as string));
        // Every refusal in the submitted buffer, not just this screen's: nothing else reports the others.
        notifyRefused(refusedScoreActionKeys(results.userSession.actions ?? [], results.recordUserSession ?? []).size, refusedScoreReasons(results.userSession.actions ?? [], results.recordUserSession ?? []));

        // The remover returns the session's REMAINING actions (read under the buffer lock,
        // so it includes anything scored mid-submit) — no second store read.
        const remaining = await removeUserSessionActions(results.user._id, results.userSession.startedAt, ackedKeys);
        // Any acknowledgement is progress, even a partial one, so the next submit runs at full
        // rate. A submit that had actions and earned none is what backs off.
        if (ackedKeys.size || !results.userSession.actions.length) resumeFullRate();
        else backOff();
        // The prune above must still run post-unmount; only the setState below is guarded.
        if (!isMounted()) return;
        setHasSaveError(saveFailed);
        setHasUnsavedScores(!!remaining && remaining.some(isMyAction));
      })
      .catch(async (err) => {
        console.error('useBufferedScoreSubmit submit failed:', err);
        const unsynced = await screenHasUnsyncedActions(user, isMyAction);
        backOff();
        if (!isMounted()) return;
        setHasSaveError(true);
        setHasUnsavedScores(unsynced);
      });
  }, [isSaving, submitScores, setHasUnsavedScores, user, isMyAction, isMounted, notifyRefused, backOff, resumeFullRate]);

  // Drive the interval off a ref so a per-render handler identity doesn't reset
  // the 5s countdown on every keystroke; the interval rebinds only on its gates.
  const submitRef = useLatestRef(handleSubmitScores);

  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() < nextAttemptAtRef.current) return;
      if (hasUnsavedScores && !isSaving && isQueueOpen && canSubmit && user) submitRef.current();
    }, 5 * 1000);
    return () => clearInterval(interval);
  }, [hasUnsavedScores, isSaving, isQueueOpen, canSubmit, user, submitRef]);

  // The error reflects the latest attempt; once nothing is unsaved (a sync landed, here
  // or via the OfflineHandler) there is no failure left to show, so clear it.
  useEffect(() => {
    if (!hasUnsavedScores) setHasSaveError(false);
  }, [hasUnsavedScores]);

  return { hasSaveError, handleSubmitScores };
};
